<script lang="ts">
    import EekLabel from '@tile/components/eek/EekLabel.svelte';
    import JsonLd from '@tile/components/json-ld/JsonLd.svelte';
    import OttoUp from '@tile/components/otto-up/OttoUp.svelte';
    import ProductComparisonCheckBox from '@tile/components/product-comparison/ProductComparisonCheckBox.svelte';
    import DealBadge from '@tile/components/product-image/DealBadge.svelte';
    import type { ImageAspectRatio } from '@tile/components/product-image/types.ts';
    import Rating from '@tile/components/rating/Rating.svelte';
    import SocialProofBadges from '@tile/components/social-proof-badges/SocialProofBadges.svelte';
    import TopInfo from '@tile/components/top-info/TopInfo.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import { shouldShowWishlistIcon } from '@tilelist/domain/feature-visibility/WishlistVisibility.ts';
    import PriceDispatcher from '@tilelist/layouts/dispatchers/price-dispatcher/PriceDispatcher.svelte';
    import ProductImageDispatcher from '@tilelist/layouts/dispatchers/product-image-dispatcher/ProductImageDispatcher.svelte';
    import WishlistIcon from '@tilelist/layouts/experiments/e3803/wishlist-icon/WishlistIcon.svelte';
    import Availability from '@tilelist/layouts/experiments/e3918/availability/Availability.svelte';
    import Card from '@tilelist/layouts/experiments/e3918/card/Card.svelte';
    import ColorThumbs from '@tilelist/layouts/experiments/e3918/color-thumbs/ColorThumbs.svelte';
    import type { DefaultLayoutV3 } from '@tilelist/layouts/experiments/e3918/default-layout/DefaultLayout.ts';
    import ProductBrand from '@tilelist/layouts/experiments/e3918/product-brand/ProductBrand.svelte';
    import ProductHeader from '@tilelist/layouts/experiments/e3918/product-header/ProductHeader.svelte';
    import ProductLink from '@tilelist/layouts/experiments/e3918/product-link/ProductLink.svelte';
    import ProductTitle from '@tilelist/layouts/experiments/e3918/product-title/ProductTitle.svelte';
    import type { TileLayoutProps } from '@tilelist/layouts/shared/TileLayoutProps.ts';

    type Props = TileLayoutProps & {
        layout: DefaultLayoutV3;
        aspectRatio: ImageAspectRatio;
        showInstallments: boolean;
        bestVariationBenefit?: string;
    };

    let {
        layout,
        aspectRatio,
        imageLoadingOptions,
        onRedirectToPdp,
        rule,
        colors,
        sponsored,
        funderLegalName,
        advertiserLegalName,
        element = $bindable(null),
        showInstallments,
        bestVariationBenefit,
    }: Props = $props();

    const onex = injectWiring().onex;
    const forceIndex = $derived(onex.isToggleActive('REPTILE_1495_FORCE_INDEX'));
    const showWishlistIcon = $derived(shouldShowWishlistIcon(onex));
    const { variation, title: productTitle, link: rawLink, showMultipleSizes } = $derived(layout);
    const link = $derived(forceIndex ? { ...rawLink, index: true } : rawLink);

    /**
 *
 *
 *
 *
 */
    const hasRating = $derived(!!(variation.customerReviews?.amount && variation.customerReviews?.averageRating));

    /**
 *
 *
 */
    const hasColors = $derived(!!(colors && colors.length > 1));
    const hasDeliveryInfo = $derived(Boolean(variation.availability?.detail || variation.ottoUp));
</script>

<Card
    data-jarvis="reptile-tile-container"
    data-variation-id={variation.variationId}
    bind:element
    disableOverlayEffect
    fullHeight
    size="50"
>
    <!-- NOTE: This is used in the Card component and is passed through -->
    {#snippet primaryAction()}
        <ProductLink id={`link-${variation.variationId}`} {link} onClick={onRedirectToPdp} />
    {/snippet}

    <div data-secondary-action>
        <ProductComparisonCheckBox currentVariation={variation} />
    </div>

    <div class="reptile-clean-layout__image">
        <ProductImageDispatcher
            images={layout.images}
            fallbackImage={layout.primaryImage}
            variationId={variation.variationId}
            detailPageLink={variation.detailPageLink}
            brand={productTitle.brand}
            title={productTitle.display}
            {imageLoadingOptions}
            onClick={onRedirectToPdp}
            {aspectRatio}
        />
        <!-- DealBadge and SocialProofBadges are self-positioning atoms (own position: absolute),
             rendered as flat siblings here rather than nested inside ProductImage,
             so both stay independently clickable/composable (see README). -->
        {#if variation.deal}
            <DealBadge deal={variation.deal} variationId={variation.variationId} {rule} />
        {/if}
        <SocialProofBadges variant="clean" socialProof={variation.socialProof} />
        {#if showWishlistIcon}
            <WishlistIcon variationId={variation.variationId} multipleSizes={variation.multipleSizes} />
        {/if}
    </div>

    <div class="reptile-clean-layout__content">
        <ProductHeader>
            <ProductBrand brand={productTitle.brand} />
            <ProductTitle name={productTitle.display} />
            <TopInfo
                infos={variation.topInfos}
                variant="clean"
                multipleSizes={showMultipleSizes && !!variation.multipleSizes}
            />
            {#if hasRating}
                <div data-secondary-action>
                    <Rating variant="clean" customerReviews={variation.customerReviews} />
                </div>
            {/if}
        </ProductHeader>

        {#if variation.energyLabels?.length}
            <div data-secondary-action>
                <EekLabel labels={variation.energyLabels} asRow />
            </div>
        {/if}

        <!-- TODO: if the cleantile experiment (E3918) is successfully finished don't forget to add the RadioEquipmentRegulation -->

        <div class="reptile-clean-layout__purchase-info">
            <div class="reptile-clean-layout__price-delivery">
                <PriceDispatcher
                    variationId={variation.variationId}
                    saleTags={variation.saleTags}
                    price={variation.price}
                    dealHighlight={variation.deal?.highlight}
                    dealId={variation.deal?.dealId}
                    {rule}
                    {showInstallments}
                    {bestVariationBenefit}
                />

                {#if hasDeliveryInfo}
                    <div class="reptile-clean-layout__row">
                        <Availability availability={variation.availability} />
                        <OttoUp show={variation.ottoUp} />
                    </div>
                {/if}
            </div>

            {#if hasColors || sponsored}
                <div class="reptile-clean-layout__row" class:reptile-clean-layout__row--end={!hasColors}>
                    <ColorThumbs {colors} />
                    <div data-secondary-action>
                        {#if sponsored || false}<ofc-sponsored-tag-v1
                                funder-legal-name={funderLegalName}
                                advertiser-legal-name={advertiserLegalName}
                            ></ofc-sponsored-tag-v1>{/if}
                    </div>
                </div>
            {/if}
        </div>
    </div>
</Card>

<JsonLd {variation} />

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@shared/styles/mixins' as reptileMixins;

    .reptile-clean-layout {
        &__image {
            position: relative;
        }

        &__content,
        &__purchase-info,
        &__price-delivery {
            display: flex;
            flex-direction: column;
        }

        &__content {
            row-gap: tokens.$oc-semantic-spacing-absolute-100;
            padding: tokens.$oc-semantic-spacing-absolute-75;
        }

        &__purchase-info {
            row-gap: tokens.$oc-semantic-spacing-absolute-50;
        }

        &__price-delivery {
            row-gap: tokens.$oc-semantic-spacing-absolute-100;
        }

        &__row {
            @include reptileMixins.row-space-between;

            gap: tokens.$oc-semantic-spacing-absolute-50;

            /*                                                                                   */
            /*                               */
            &--end {
                justify-content: flex-end;
            }
        }
    }
</style>
